<template>
  <div
    class="field-item"
    :class="{
      'field-item--disabled': disabled,
      'field-item--dragging': isDragging
    }"
    draggable="true"
    @click="handleClick"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <!-- Field Icon -->
    <div class="field-item__icon">
      <v-icon
        :color="fieldIconColor"
        size="20"
      >
        {{ fieldIcon }}
      </v-icon>
    </div>

    <!-- Field Info -->
    <div class="field-item__info">
      <div class="field-item__name">
        {{ field.displayName }}
      </div>
      <div class="field-item__path">
        {{ field.fieldName }}
      </div>
    </div>

    <!-- Field Metadata -->
    <div class="field-item__metadata">
      <!-- Used indicator -->
      <div v-if="isUsed" class="field-item__used-indicator">
        <v-icon
          :color="usageIndicatorColor"
          size="16"
          class="mr-1"
        >
          mdi-check-circle
        </v-icon>
        <span v-if="usageCount > 1" class="field-item__usage-count">
          {{ usageCount }}
        </span>
      </div>
      
      <!-- Field Flags -->
      <div class="field-item__flags">
        <!-- Incremental field indicator removed as requested -->
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'

interface CassieField {
  fieldName: string
  displayName: string
  typeFriendlyName: string
  dataType: string
  isFilterable: boolean
  isIncremental: boolean
  group: string
}

export default Vue.extend({
  name: 'FieldItem',
  props: {
    field: {
      type: Object as () => CassieField,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isUsed: {
      type: Boolean,
      default: false
    },
    usageCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isDragging: false
    }
  },
  computed: {

    /**
     * Get icon for field type
     */
    fieldIcon(): string {
      switch (this.field.typeFriendlyName.toLowerCase()) {
        case 'date':
          return 'mdi-calendar'
        case 'number':
          return 'mdi-numeric'
        case 'text':
          return 'mdi-text'
        case 'guid':
          return 'mdi-identifier'
        case 'boolean':
          return 'mdi-toggle-switch'
        default:
          return 'mdi-database'
      }
    },

    /**
     * Get color for field icon
     */
    fieldIconColor(): string {
      if (this.disabled) return '#565a61'
      
      // Use consistent dark grey for all field types
      return '#565a61' // Dark grey from existing palette
    },

    /**
     * Get color for usage indicator
     */
    usageIndicatorColor(): string {
      return '#406A94' // Steel Blue from brand palette
    },
  },
  methods: {
    /**
     * Handle field click
     */
    handleClick(): void {
      if (this.disabled) return
      this.$emit('click', this.field)
    },

    /**
     * Handle drag start
     */
    handleDragStart(event: DragEvent): void {
      this.isDragging = true
      
      // Set drag data for field information
      if (event.dataTransfer) {
        try {

          event.dataTransfer.effectAllowed = 'copy'
          // Only set JSON data, not plain text to avoid browser's default text insertion
          event.dataTransfer.setData('application/json', JSON.stringify(this.field))
          // Set a custom type for our field data
          event.dataTransfer.setData('application/x-cassie-field', this.field.fieldName)
        }
        catch (error) {
          console.error('Error setting drag data:', error)
        }
      }
      
      this.$emit('dragstart', this.field)
    },

    /**
     * Handle drag end
     */
    handleDragEnd(): void {
      this.isDragging = false
      this.$emit('dragend', this.field)
    }
  }
})
</script>

<style lang="scss" scoped>
.field-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid transparent;

  &:hover:not(&--disabled) {
    background: rgba(64, 106, 148, 0.04);
  }

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &--dragging {
    opacity: 0.8;
    background: rgba(64, 106, 148, 0.08);
  }

  &__icon {
    margin-right: 12px;
    display: flex;
    align-items: center;
    min-width: 20px;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 500;
    color: #242529;
    line-height: 1.2;
    margin-bottom: 2px;
  }

  &__path {
    font-size: 12px;
    color: #565a61;
    line-height: 1.2;
    font-family: 'Courier New', monospace;
  }

  &__metadata {
    margin-left: 8px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }

  &__used-indicator {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #406A94;
    font-weight: 500;
  }

  &__usage-count {
    font-size: 11px;
    background: #406A94;
    color: white;
    border-radius: var(--radius-full);
    padding: 2px 6px;
    min-width: 18px;
    text-align: center;
    line-height: 1;
  }

  &__flags {
    display: flex;
    gap: 4px;
    align-items: center;
  }
}
</style>